import type { ReactNode } from 'react'
import invariant from 'tiny-invariant'
import { fbt } from 'fbtee'
import { youPillLabel } from '../lib/commonStrings'
import { UserAvatar, type UserAvatarSize } from './UserAvatar'
import { Skeleton } from './ui/skeleton'
import { useForceLoading } from '../lib/skeletonDebug'
import { favoriteRingClass } from '../lib/favoriteRing'
import type { UserProfile } from '../types'
import { clickableProps } from '../lib/a11y'

interface Props {
  loading?: boolean
  name?: string
  profile?: UserProfile
  isYou?: boolean
  isCreator?: boolean
  /** Group admin pill; the Creator pill wins when both are set. */
  isAdmin?: boolean
  /** Renders the golden ring on the avatar (favorited friend). */
  favorite?: boolean
  subtitle?: ReactNode
  size?: UserAvatarSize
  action?: ReactNode
  onClick?: () => void
}

export function UserRow({ loading: loadingProp, name, profile, isYou, isCreator, isAdmin, favorite, subtitle, size, action, onClick }: Props) {
  const loading = useForceLoading(loadingProp)
  if (loading) {
    return (
      <li className="flex items-center gap-2.5">
        <Skeleton className="size-8 rounded-full shrink-0" />
        <Skeleton className="h-4 w-28" />
      </li>
    )
  }

  invariant(name !== undefined, 'UserRow: name is required when not loading')

  return (
    <li className="flex items-center justify-between gap-2">
      <div
        className={`flex items-center gap-2.5 min-w-0 ${onClick ? 'cursor-pointer' : ''}`}
        {...clickableProps(onClick)}
      >
        <UserAvatar profile={profile} size={size} className={favorite ? favoriteRingClass : undefined} />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm truncate">{name}</span>
            {isYou && (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                {youPillLabel()}
              </span>
            )}
            {isCreator && (
              <span className="text-[10px] uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                {fbt('Creator', "Pill shown next to the group creator's name in the members list")}
              </span>
            )}
            {isAdmin && !isCreator && (
              <span className="text-[10px] uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                {fbt('Admin', "Pill shown next to a group admin's name in the members list")}
              </span>
            )}
          </div>
          {subtitle && (
            <span className="text-xs text-muted-foreground truncate">{subtitle}</span>
          )}
        </div>
      </div>
      {action}
    </li>
  )
}
